import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { auth } from '../auth/firebaseAuth';
import { getConversationId } from './conversationId';
import { subscribePrivateChat, sendPrivateMessage, deletePrivateConversation } from './privateChatFirestore';
import {
  findFriendOnSquad,
  getMessagingBlockedIdSet,
  isMessagingBlockedWithPlayer,
} from './messagingPolicy';
import {
  mapPrivateChatSubscribeError,
  mapPrivateChatSendError,
  mapPrivateChatDeleteError,
} from './errors';
import { RATE_LIMIT_MS, MAX_MESSAGE_LENGTH } from './constants';

/**
 * Active 1:1 thread: subscribe, send, validation (Lockbox UI).
 */
export function usePrivateChatThread({
  useCloudFriends,
  currentUserId,
  user,
  privateChatWith,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const lastSendAtRef = useRef(0);

  const resolvedMyUid = useCloudFriends
    ? (currentUserId || auth.currentUser?.uid || user?.id)
    : user?.id;

  const friendsMessagingKey = (user?.friends ?? [])
    .map((f) => `${f.id}:${f.blockedByMe ? 1 : 0}:${f.blocksMe ? 1 : 0}`)
    .sort()
    .join('|');

  const messagingBlockedIdSet = useMemo(
    () => getMessagingBlockedIdSet(user?.friends ?? []),
    [friendsMessagingKey]
  );

  useEffect(() => {
    const myUid = resolvedMyUid;
    const convId = getConversationId(myUid, privateChatWith?.id);
    if (!convId || (privateChatWith?.id && messagingBlockedIdSet.has(privateChatWith.id))) {
      setMessages((prev) => (prev.length === 0 ? prev : []));
      setError((prev) => (prev === null ? prev : null));
      return undefined;
    }
    setError(null);
    return subscribePrivateChat(
      convId,
      myUid ?? '',
      (next) => {
        setMessages(next);
        setError(null);
      },
      (err) => setError(mapPrivateChatSubscribeError(err))
    );
  }, [resolvedMyUid, privateChatWith?.id, friendsMessagingKey, useCloudFriends]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    const displayName = user?.name || user?.username || 'Player';
    if (!text || !privateChatWith?.id) return;

    const fromUserId = resolvedMyUid;
    if (useCloudFriends && !fromUserId) {
      setError('Sign in with a real account to send private messages.');
      return;
    }
    if (!fromUserId) return;

    const chatFriend = findFriendOnSquad(privateChatWith.id, user?.friends ?? []);
    if (!chatFriend) {
      setError('You can only message friends on your squad.');
      return;
    }
    if (isMessagingBlockedWithPlayer(privateChatWith.id, user?.friends ?? [])) {
      setError(
        chatFriend.blockedByMe
          ? 'You blocked this player. Unblock them in Friends to send messages.'
          : 'You cannot message this player while they have you blocked.'
      );
      return;
    }

    const now = Date.now();
    if (now - lastSendAtRef.current < RATE_LIMIT_MS) return;
    const convId = getConversationId(fromUserId, privateChatWith.id);
    if (!convId) return;

    try {
      setError(null);
      await sendPrivateMessage({
        conversationId: convId,
        fromUserId,
        fromUserName: displayName,
        text,
      });
      lastSendAtRef.current = now;
      setInput('');
    } catch (err) {
      console.error('[privateChat] send failed:', err);
      setError(mapPrivateChatSendError(err));
    }
  }, [
    input,
    privateChatWith?.id,
    resolvedMyUid,
    useCloudFriends,
    user?.friends,
    user?.name,
    user?.username,
  ]);

  const clearInput = useCallback(() => setInput(''), []);

  const onInputChange = useCallback((value) => {
    setInput(String(value).slice(0, MAX_MESSAGE_LENGTH));
  }, []);

  const deleteChat = useCallback(async () => {
    const fromUserId = resolvedMyUid;
    const friendId = privateChatWith?.id;
    const convId = getConversationId(fromUserId, friendId);
    if (!convId || !fromUserId || !friendId) return false;

    if (isMessagingBlockedWithPlayer(friendId, user?.friends ?? [])) {
      setError('Messaging is blocked for this player.');
      return false;
    }

    try {
      setError(null);
      await deletePrivateConversation(convId);
      setMessages([]);
      setInput('');
      return true;
    } catch (err) {
      console.error('[privateChat] delete failed:', err);
      setError(mapPrivateChatDeleteError(err));
      return false;
    }
  }, [resolvedMyUid, privateChatWith?.id, user?.friends]);

  return {
    messages,
    error,
    input,
    onInputChange,
    sendMessage,
    deleteChat,
    clearInput,
    maxLength: MAX_MESSAGE_LENGTH,
  };
}
